/* eslint-disable camelcase */
import { createPremiumIcon, node } from "~/util/dom";
import MyMapsPopup from "~/es6/mymaps.popup";
import ReportFormFieldDetail from "./form/detail";
import ReportFormFieldInput from "./form/input";
import ReportFormFieldLoader from "./form/loader";
import ReportFormFieldMap from "./form/map";
import ReportFormFieldPhotos from "./form/photos";
import ReportFormFieldButton from "./form/button";
import ReportFormFieldTextarea from "./form/textarea";
import ReportFormFieldSwitch from "./form/switch";
import { SUBMIT_ERROR, SMALL_MEDIA_QUERY, DEBUG } from "./const";
import { sendToMantis, getScreenshot, getAttachment, newReport, getScreenshotMediaId } from "./provider";
import { ISubmitData, IAttachment, IReportOpts, TReportOnUpdate, IPopupContent, INewReportData } from "./interfaces";
import { createThankYou, getSendData } from "./util";
import { REVIEW_THANK_YOU_TO } from "~/review/const";
import { makeDraw } from "./draw";
import Dialog from "~/ui/dialog";
import { _ } from "~/i18n";
import Scene from "~/scene";
import { makeEvent } from "~/lib/signals";
import { store } from "~/state/store";
import { addActiveContent, removeActiveContent } from "~/state/ribbon/state";
import { TActiveContent } from "~/ribbon/interfaces";

interface IConfirmOptions {
	text: string;
	confirmText: string;
	rejectText: string;
}

export default class Report extends MyMapsPopup {
	#opts: IReportOpts;

	#state: {
		attachment?: File;
		hints?: object;
		openingMediaId?: number;
	} = {};

	#originalValues: object;

	#content: { [key: number]: IPopupContent };

	#contentId: number;

	#fields = new Map();

	#updateStateHandlers: Array<(scope: Report, name: string, nextState: any) => void> = [];

	#name: string;

	#header: HTMLDivElement;

	#body: HTMLDivElement;

	#form: HTMLDivElement;

	#loaderImage: HTMLImageElement;

	#error: HTMLParagraphElement;

	#footer: HTMLDivElement;

	#overlay: HTMLDivElement;

	#confirmation: HTMLDivElement;

	#submitButton: HTMLButtonElement;

	#submittability;

	#confirmability;

	#confirmOptions: IConfirmOptions = {
		confirmText: "",
		rejectText: "",
		text: "",
	};

	#isConfirming = false;

	#onUpdate: TReportOnUpdate;

	#onResponsiveClose: boolean;

	#mediaQuery: any;

	#changeListener: () => void;

	#draw: ReturnType<typeof makeDraw>;

	#contentName: TActiveContent;

	constructor(opts: IReportOpts) {
		super({
			closeSvg: "close-v2",
			...opts.holderParent
				? {
					holderParent: opts.holderParent,
				}
				: {},
		});

		this.#opts = opts;
		this.#originalValues = opts.values;
		this.#name = opts.name || "report";
		this.#contentName = "report";
		this.#content = opts.popupContent;
		this.#contentId = 0;
		this.#onUpdate = opts.onUpdate;
		this.#onResponsiveClose = typeof opts.onResponsiveClose === "boolean" ? opts.onResponsiveClose : true;
		this.#mediaQuery = null;
		this.#changeListener = () => {
			if (this.#mediaQuery?.matches) {
				this.close();
			}
		};
		this.#draw = null;

		this.#build();
		window.addEventListener("resize", this.#checkOverflow);
		store.dispatch(addActiveContent(this.#contentName));
	}

	#build() {
		const cover = this.getCover();
		const container = this.getContainer();

		cover.classList.add(`${this.#opts.name}-cover`, "transition", "transition-start");
		container.classList.add("transition", "report", "transition-start");

		this.#header = node("div", { className: "header" });
		const title = node("span", { className: "title" });

		this.#body = node("div", { className: "body" });
		this.#form = node("div", { className: "form" });

		// info o premiu
		const { premium } = store.getState().user.user;

		if (premium) {
			const premiumInfo = node("div", { className: "report-dialog-premium-info" });

			premiumInfo.append(createPremiumIcon(), node("p", { innerHTML: _("report.premiumInfo") }));
			this.#body.appendChild(premiumInfo);
		}

		this.#body.append(this.#form);

		const footerPlaceholder = node("div", { className: "footer" });

		this.#footer = node("div", { className: "footer container" });

		this.#header.append(title);

		this.#submitButton = node("button", { className: "approve" });
		this.#submitButton.setAttribute("data-log-ignore", "1");
		const submitButtonTooltip = node("span", { className: "approve-notification" });
		const submitButtonTitle = node("span", { textContent: _("submit") });

		this.#loaderImage = node("img", { src: "/img/loader.gif", className: "loader" });
		this.#error = node("p", { className: "error" });

		this.#submitButton.append(submitButtonTooltip, submitButtonTitle);
		this.#footer.append(
			this.#submitButton,
			this.#loaderImage,
			this.#error,
		);

		this.#overlay = node("div", { className: "overlay" });
		this.#confirmation = node("div", { className: "confirmation" });
		this.#overlay.append(this.#confirmation);

		container.append(this.#header, this.#body, footerPlaceholder, this.#footer, this.#overlay);
		container.addEventListener("resize-content", this.#checkOverflow);
		this.#body.addEventListener("scroll", () => {
			if (this.#body.scrollTop > 0) {
				this.#header.classList.add("scrolled");
			} else {
				this.#header.classList.remove("scrolled");
			}
		});

		this.#confirmation.addEventListener("click", this);
		this.#submitButton.addEventListener("click", this);

		this.#setContent();
		this.#checkSubmittability(this.#state);
	}

	#checkOverflow = () => {
		if (this.#body.scrollHeight > this.#body.clientHeight) {
			this.#footer.classList.add("overflow");
		} else {
			this.#footer.classList.remove("overflow");
		}
	};

	#setContent() {
		const content = this.#content[this.#contentId];
		const { isExpanded, isMaximal, header, fields, submittability, confirmability, confirmOptions } = content;
		const container = this.getContainer();

		if (isExpanded) {
			container.classList.add("expanded");
		} else {
			container.classList.remove("expanded");
		}

		if (isMaximal) {
			container.parentElement.style.left = "unset";
			container.parentElement.style.right = "0";
			container.parentElement.style.width = "unset";
			container.parentElement.style.justifyContent = "flex-end";
			container.classList.add("maximal");
		} else {
			container.parentElement.style.justifyContent = "";
			container.classList.remove("maximal");
		}

		this.#header.firstElementChild.innerText = header;
		this.#submittability = submittability;
		this.#confirmability = confirmability;
		this.#confirmOptions = confirmOptions;

		for (const field of fields) {
			let fieldObject = null;

			switch (field.type) {
				case "input":
					fieldObject = new ReportFormFieldInput({
						container,
						parent: this.#form,
						parentName: this.#name,
						onChange: this.#setValue("input", field.name),
						...field,
					});
					break;
				case "map":
					fieldObject = new ReportFormFieldMap({
						container,
						parent: this.#form,
						parentName: this.#name,
						onChange: this.#setValue("map", field.name),
						...field,
					});
					break;
				case "button":
					fieldObject = new ReportFormFieldButton({
						container,
						parent: this.#form,
						parentName: this.#name,
						onChange: this.#setValue("button", field.name),
						...field,
					});
					break;
				case "photos":
					fieldObject = new ReportFormFieldPhotos({
						container,
						parent: this.#form,
						submitError: SUBMIT_ERROR,
						onChange: this.#setValue("photos", field.name),
						onUploadStart: () => {
							this.#submitButton.setAttribute("disabled", "true");
							this.#submitButton.firstElementChild.innerText = _("sending");
						},
						newType: this.#isNewType(),
						...field,
					});
					break;
				case "textarea":
					fieldObject = new ReportFormFieldTextarea({
						container,
						parent: this.#form,
						parentName: this.#name,
						onChange: this.#setValue("textarea", field.name),
						...field,
					});
					break;
				case "loader":
					fieldObject = new ReportFormFieldLoader({
						container,
						parent: this.#form,
						parentName: this.#name,
						onChange: this.#setValue("loader", field.name),
						...field,
					});
					break;
				case "switch":
					fieldObject = new ReportFormFieldSwitch({
						container,
						parent: this.#form,
						parentName: this.#name,
						onChange: this.#setValue("switch", field.name),
						...field,
					});
					break;
				case "detail":
					fieldObject = new ReportFormFieldDetail({
						container,
						parent: this.#form,
						parentName: this.#name,
						onChange: this.#setState("detail", field.name),
						onUploadStart: () => {
							this.#showError("");
							this.#submitButton.setAttribute("disabled", "true");
							this.#submitButton.firstElementChild.innerText = _("sending");
						},
						onUploadError: () => {
							this.#showError(_("firmPhotos.error.photoUpload"));
						},
						...field,
					});
					break;
				default:
			}

			if (fieldObject) {
				this.#fields.set(`${field.type}-${field.name}`, fieldObject);
			}

			if (typeof field.updateStateHandler === "function") {
				this.#updateStateHandlers.push(field.updateStateHandler);
			}
		}

		if (this.#opts.createDraw) {
			this.#draw = makeDraw(state => {
				switch (state) {
					case "was-drawed":
						this.#updateState("draw-util", () => ({
							...this.#state,
							drawUtil: this.#draw.getCoords(),
						}));
						break;

					case "clear":
						this.#updateState("draw-util", () => ({
							...this.#state,
							drawUtil: [],
						}));
						break;

					default:
				}
			});
		}
	}

	#clearContent() {
		this.#form.replaceChildren();
		this.#state = {};
		this.#fields.clear();
		this.#updateStateHandlers = [];
		this.#submittability = null;
		this.#confirmability = null;
		this.#confirmOptions = {
			confirmText: "",
			rejectText: "",
			text: "",
		};
	}

	setOnUpdate(onUpdate: TReportOnUpdate) {
		this.#onUpdate = onUpdate;
	}

	changeContent(index: number) {
		if (index !== this.#contentId) {
			this.#contentId = index;
			this.#clearContent();
			this.#setContent();
			this.#checkOverflow();
			this.#updateState("change-content", state => state);
		}
	}

	getState() {
		return this.#state;
	}

	getField(name: string) {
		return this.#fields.get(name);
	}

	#setValue = (fieldType: string, fieldName: string) => (value: string, data: object) => {
		const valueObject = value || (typeof value === "string" && fieldName === "name") ? { [fieldName]: value } : {};
		const dataObject = data ? { [`${fieldName}-data`]: data } : {};

		this.#updateState(
			`${fieldType}-${fieldName}`,
			state => {
				const { [fieldName]: deleted, [`${fieldName}-data`]: deleted2, ...reducedState } = state;

				return { ...reducedState, ...valueObject, ...dataObject };
			},
		);
	};

	#setState = (fieldType: string, fieldName: string) => (fieldState: any) => {
		this.#updateState(
			`${fieldType}-${fieldName}`,
			state => Object.entries(fieldState).reduce((acc, current) => {
				const [key, value] = current;

				return { ...acc, [key]: value };
			}, state),
		);
	};

	#checkSubmittability(state) {
		if (typeof this.#submittability === "function") {
			const notification = this.#submittability(state, this.#originalValues);

			if (notification) {
				this.#submitButton.firstElementChild.innerText = notification;
				this.#submitButton.setAttribute("disabled", "");
			} else {
				this.#submitButton.firstElementChild.innerText = "";
				this.#submitButton.removeAttribute("disabled");
			}
		}
	}

	#updateState(name: string, update: (data: any) => any) {
		if (typeof update === "function") {
			const nextState = update(this.#state);

			this.#checkSubmittability(nextState);
			this.#state = nextState;

			for (const handler of this.#updateStateHandlers) {
				handler(this, name, nextState);
			}
		}
	}

	/* eslint-disable class-methods-use-this */
	#startLoader() {
		this.getContainer().classList.add("loading");
	}

	#endLoader() {
		this.getContainer().classList.remove("loading");
	}

	#showError(msg: string) {
		this.#error.textContent = msg || "";
	}

	#isNewType() {
		const reportType = this.#opts.type;

		return Mapy.CUS.userweb?.reportLocalChanges && ["place-add", "place-edit", "place-open-again"].includes(reportType);
	}

	async #onSubmit() {
		const { attachment, openingMediaId } = this.#state;
		const promises = [];
		const newType = this.#isNewType();

		this.#startLoader();

		if (attachment) {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			promises.push(getAttachment(attachment, newType));
		}

		for (const fieldObject of this.#fields.values()) {
			// jestliže některé pole formuláře používá data formuláře před jejich odesláním,
			// provedeme toto použití dat a uložíme si příslib jeho výsledku
			if (typeof fieldObject.onSubmit === "function") {
				promises.push(fieldObject.onSubmit(this.#state));
			}
		}

		// screenshot
		if (newType) {
			promises.push(getScreenshotMediaId());
		} else {
			promises.push(getScreenshot(newType));
		}

		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		try {
			const errors = [];
			const attachments = [];
			let mediaIds = [];
			const mediaIdsNew: INewReportData["media_ids"] = [];
			const itemsData = await Promise.all(promises);

			if (openingMediaId) {
				mediaIdsNew.push({
					media_id: openingMediaId,
					type: "opening",
				});
			}

			itemsData.forEach((item: ISubmitData) => {
				// pro screenshot preskocime
				if (item.errorMsg && item.type === "screenshot") {
					return;
				}

				if (item.errorMsg) {
					errors.push(item.errorMsg);
				} else {
					// pridame data podle typu
					switch (item.type) {
						case "photos":
							if (newType) {
								for (const media_id of item.mediaIds) {
									mediaIdsNew.push({
										media_id,
										type: "photo",
									});
								}
							} else {
								mediaIds = item.mediaIds;
							}
							break;
						case "screenshot":
							if (newType) {
								if (item.mediaId) {
									mediaIdsNew.push({
										media_id: item.mediaId,
										type: "screenshot",
									});
								}
							} else {
								attachments.unshift(item.screenShot);
							}
							break;
						case "attachment":
							attachments.push(item.attachment);
							break;
						default:
					}
				}
			});

			if (errors.length) {
				this.#endLoader();
				this.#showError(errors.join(", "));
			} else {
				this.#sendData(mediaIds, attachments, mediaIdsNew);
			}
		} catch (exc) {
			/* eslint-disable no-console */
			console.log(exc);
		}
	}

	async #sendData(mediaIds: Array<number>, attachments: Array<IAttachment>, mediaIdsNew: INewReportData["media_ids"]) {
		const { user } = store.getState().user;

		this.#showError("");

		try {
			const hasPhotos = mediaIds.length > 0;
			const hasAttachPhotos = attachments.filter(item => !item.screenshot).length > 0;

			// new type
			if (this.#isNewType()) {
				console.log({ mediaIdsNew });

				const transformedData = this.#opts.transformDataNew(this.#state, mediaIdsNew);
				const requestData = await newReport(transformedData);

				console.log({ transformedData, requestData });

				// chyba
				if (requestData.error) {
					this.#showError(_("report.sendError"));
					this.#endLoader();

					return;
				}

				if (typeof this.#opts.onSendNew === "function") {
					this.#opts.onSendNew(transformedData.reportData, {
						hasPhotos,
						hasAttachPhotos,
						opening: transformedData.opening,
						source: transformedData.source,
						id: transformedData.id,
					});
				}
			} else {
				/* eslint-disable-next-line */
				const sendData = await getSendData({
					state: this.#state,
					project: this.#opts.submitOptions.project,
					rusId: user.state === "green" && !user.phoneOnly
						? user.rusId
						: 0,
					mediaIds,
					attachments,
					transformData: this.#opts.transformData,
				});

				DEBUG && console.log(["sendData", sendData]);

				await sendToMantis(sendData.note, sendData.mantisData, sendData.hints);

				if (typeof this.#opts.onSend === "function") {
					this.#opts.onSend(sendData, {
						hasPhotos,
						hasAttachPhotos,
					});
				}
			}

			this.close();
			this.#showThankYou();
		} catch (exc) {
			/* eslint-disable no-console */
			console.log(exc);
			this.#showError(_("report.sendError"));
			this.#endLoader();
		}
	}

	#showConfirmation(options: IConfirmOptions) {
		const { text, confirmText, rejectText } = options;

		if (this.#opts.dialogConfirmation) {
			const dialog = new Dialog({
				title: confirmText,
				desc: text,
				onClose: event => {
					dialog.close();
					this.#hideConfirmation();

					event.e.stopPropagation();
				},
			});

			dialog.addBtn({
				title: _("close2"),
				type: "error",
				bindEnter: true,
				cb: () => {
					dialog.close();
					this.close();
				},
			});
			dialog.addBtn({
				title: _("cancel"),
				cb: () => {
					dialog.close();
					this.#hideConfirmation();
				},
			});
			dialog.open();
		} else {
			const nodes = [node("span", { innerText: text })];

			if (confirmText) {
				const btn = node("button", { innerText: confirmText, className: "confirm-button" });

				btn.setAttribute("data-log-ignore", "1");
				nodes.push(btn);
			}

			if (rejectText) {
				const btn = node("button", { innerText: rejectText, className: "reject-button" });

				btn.setAttribute("data-log-ignore", "1");
				nodes.push(btn);
			}

			this.#confirmation.append(...nodes);
			this.#overlay.classList.add("active");

			if (confirmText || rejectText) {
				this.#confirmation.querySelector("button").focus();
			}
		}

		this.#isConfirming = true;
	}

	#hideConfirmation() {
		this.#overlay.classList.remove("active");
		this.#isConfirming = false;

		if (!this.#opts.dialogConfirmation) {
			setTimeout(() => {
				this.#confirmation.replaceChildren();
			}, 300);
		}
	}

	#close() {
		let isConfirmable = false;

		if (typeof this.#confirmability === "function") {
			isConfirmable = this.#confirmability(this.#state, this.#originalValues);
		}

		if (this.#draw && !isConfirmable) {
			isConfirmable = this.#draw.getCoords().length > 0;
		}

		if (isConfirmable) {
			this.#showConfirmation(this.#confirmOptions);
		} else {
			this.close();
		}
	}

	#showThankYou() {
		const thankYouElem = createThankYou();

		Scene.getContainer().append(thankYouElem);
		setTimeout(() => {
			thankYouElem.remove();
		}, REVIEW_THANK_YOU_TO);
	}

	handleEvent(event: MouseEvent | KeyboardEvent) {
		switch (event.type) {
			// kdyz je popup, nechceme, aby to propadlo az do body dropu
			case "drop":
				event.preventDefault();
				event.stopPropagation();
				break;
			case "click":
				if (this.#isConfirming) {
					if (event.target.classList.contains("confirm-button")) {
						this.close();
					} else if (event.target.classList.contains("reject-button")) {
						this.#hideConfirmation();
					}
				} else if (event.target === this._dom.cover || event.target === this._dom.closeBtn || this._dom.closeBtn.contains(event.target)) {
					event.stopPropagation();
					this.#close();
				} else if (this.#submitButton.contains(event.target)) {
					this.#onSubmit();
				}
				break;

			case "keydown": {
				const keyCode = event.which || event.keyCode;

				if (keyCode === 9) {
					if (this.#isConfirming) {
						event.preventDefault();
						const confirmButtons: Array<HTMLButtonElement> = Array.from(this.#confirmation.getElementsByTagName("button"));

						if (confirmButtons.length) {
							const { activeElement } = document;
							const index = confirmButtons.indexOf(activeElement as HTMLButtonElement);

							if (confirmButtons.length === 2 && index === 0) {
								confirmButtons[1].focus();
							} else {
								confirmButtons[0].focus();
							}
						}
					}
				} else if (keyCode === 27) {
					event.stopPropagation();
					if (this.#isConfirming) {
						this.#hideConfirmation();
					} else {
						this.#close();
					}
				}
			} break;
			default:
		}
	}

	open() {
		const cover = this.getCover();
		const container = this.getContainer();

		container.addEventListener("transitionend", () => {
			this.#onUpdate && this.#onUpdate("start-anim-end");

			if (this.#opts.staticHeight) {
				container.style.transition = "none";
				container.style.setProperty("max-height", `${container.offsetHeight}px`, "important");
				this.#body.style.setProperty("max-height", `${this.#body.offsetHeight + 1}px`, "important");
			}
		});
		super.open();
		this.#checkOverflow();
		this.#onUpdate && this.#onUpdate("start-anim-begin");

		cover.classList.add("transition-end");
		cover.classList.remove("transition-start");
		container.classList.add("transition-end");
		container.classList.remove("pop-out");
		container.classList.remove("transition-start");

		if (this.#onResponsiveClose) {
			if (!this.#mediaQuery) {
				this.#mediaQuery = window.matchMedia(SMALL_MEDIA_QUERY);
			}

			this.#mediaQuery.addEventListener("change", this.#changeListener);
		}

		makeEvent("report-open", this);
	}

	close() {
		const cover = this.getCover();
		const container = this.getContainer();

		cover.classList.add("transition-start");
		cover.classList.remove("transition-end");
		container.classList.add("pop-out");
		container.classList.add("transition-start");
		container.classList.remove("transition-end");
		container.removeEventListener("resize-content", this.#checkOverflow);
		window.removeEventListener("resize", this.#checkOverflow);
		this.#fields.clear();

		if (this.#draw) {
			this.#draw.destroy();
		}

		this.#onUpdate && this.#onUpdate("close");
		this.#onUpdate = null;

		if (this.#onResponsiveClose) {
			this.#mediaQuery.removeEventListener("change", this.#changeListener);
		}

		// konec animace
		if (this.#opts.staticHeight) {
			container.style.transition = "";
		}

		container.addEventListener("transitionend", () => {
			super.close();
		});

		makeEvent("report-close", this);
		store.dispatch(removeActiveContent(this.#contentName));
	}
}
